import { useState, useEffect } from 'react';
import TaulukkoFooter from '../../TaulukkoFooter';
import TableLoadingOverlay from './TableLoadingOverlay';

// Pure view, matching the original OhuthieBlock.jsx table layout exactly
// (same C/CH/inp/sel style constants, same hyphenated header line-breaks,
// same centered italic-placeholder Tunnus cell). v1 has no per-row delete
// button at all — a kohde IS the row there, 1:1 by construction — so this
// doesn't expose one either; testi rows are only ever created/removed via
// Poränäyte checking/unchecking a sample's testi types.
const LUOKKA_OPTIONS = ['Erinomainen', 'Hyvä', 'Tyydyttävä', 'Välttävä', 'Huono'];
const PAKKASRAPAUTUNEISUUS_OPTIONS = ['Ei rapautumaa', 'Vähäistä', 'Kohtalaista', 'Voimakasta', 'Ei koossapysyvää'];

const C  = { border: '1px solid #adb5bd', padding: '6px 8px', fontSize: '0.9rem', verticalAlign: 'middle' };
const CH = { ...C, background: '#04aa00', color: '#fff', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' };
const inp = { border: '1px solid #ced4da', borderRadius: 3, padding: '3px 6px', fontSize: '0.9rem', width: '100%', background: '#fff' };
const sel = { ...inp, cursor: 'pointer', textAlign: 'center' };

// Same mobile card pattern as VetolujuusView.jsx — this table is the same
// rows-per-sample shape (one testi-type row per eligible sample), so below
// 768px each row becomes its own card instead of a wide table row.
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

// Shared by both the desktop table row and the mobile card below.
function fieldEntries(t, patchArvo) {
  return [
    {
      label: 'Laatuluokka', node: (
        <select style={sel} value={t.arvo?.laatuluokka ?? ''} onChange={e => patchArvo(t, 'laatuluokka', e.target.value)}>
          <option value="">–</option>
          {LUOKKA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ),
    },
    {
      label: 'Kuntoluokka', node: (
        <select style={sel} value={t.arvo?.kuntoluokka ?? ''} onChange={e => patchArvo(t, 'kuntoluokka', e.target.value)}>
          <option value="">–</option>
          {LUOKKA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ),
    },
    {
      label: 'Pakkasrapautuneisuus', node: (
        <select style={{ ...inp, textAlign: 'center' }} value={t.arvo?.pakkasrapautuneisuus ?? ''} onChange={e => patchArvo(t, 'pakkasrapautuneisuus', e.target.value)}>
          <option value="">–</option>
          {PAKKASRAPAUTUNEISUUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ),
    },
  ];
}

const cardStyle = { border: '1px solid #e2e4e7', borderRadius: 10, padding: '12px 14px', marginBottom: 12, background: '#fff' };
const cardFieldLabelStyle = { fontSize: '0.72rem', color: '#6c757d', flexShrink: 0, width: 150 };
const cardFieldRowStyle = (first) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: first ? 'none' : '1px solid #f1f3f5' });

export default function OhuthieView({ eligible, rows, loading, addRow, patchArvo, caption, commitCaption, captionKohdeId, taulukkoNum = 1 }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ overflowX: 'auto', position: 'relative', minHeight: loading ? 80 : undefined }}>
      <TableLoadingOverlay show={loading} />
      {eligible.length === 0 ? (
        <div style={{ color: '#6c757d', fontSize: '0.82rem' }}>Ei näytteitä joilla on "Ohuthie" valittuna testiksi (Poränäyte-lohko).</div>
      ) : isMobile ? (
        <div>
          {eligible.map(s => {
            const t = (rows[s.id] || [])[0];
            const rakenneosaNimi = s.rakenneosa?.nimi ?? '—';
            if (!t) return (
              <div key={s.id} style={cardStyle}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 6 }}>{rakenneosaNimi} <span style={{ color: '#198754' }}>· {s.nayte_tunnus}</span></div>
                <em style={{ color: '#adb5bd' }}>Ladataan… </em>
                <button type="button" onClick={() => addRow(s)} style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem' }}>Yritä uudelleen</button>
              </div>
            );
            return (
              <div key={t.id} style={cardStyle}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>{rakenneosaNimi} <span style={{ color: '#198754' }}>· {s.nayte_tunnus || '—'}</span></div>
                {fieldEntries(t, patchArvo).map((f, i) => (
                  <div key={f.label} style={cardFieldRowStyle(i === 0)}>
                    <div style={cardFieldLabelStyle}>{f.label}</div>
                    <div style={{ flex: 1 }}>{f.node}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={CH}>Rakenneosa</th>
              <th style={CH}>Tunnus</th>
              <th style={CH}>Laatu-<br />luokka</th>
              <th style={CH}>Kunto-<br />luokka</th>
              <th style={CH}>Pakkasrapau-<br />tuneisuus</th>
            </tr>
          </thead>
          <tbody>
            {eligible.map(s => {
              // Rows auto-create the moment a sample becomes eligible (see
              // useTestiTypeV2) — this only shows for the brief window
              // before that lands, or if creation failed and needs a retry.
              const t = (rows[s.id] || [])[0];
              if (!t) return (
                <tr key={s.id}>
                  <td style={{ ...C, textAlign: 'center' }}>{s.rakenneosa?.nimi ?? '—'}</td>
                  <td style={{ ...C, textAlign: 'center', fontWeight: 500 }}>{s.nayte_tunnus}</td>
                  <td style={C} colSpan={3}>
                    <em style={{ color: '#adb5bd' }}>Ladataan… </em>
                    <button type="button" onClick={() => addRow(s)} style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem' }}>Yritä uudelleen</button>
                  </td>
                </tr>
              );
              return (
                <tr key={t.id}>
                  <td style={{ ...C, textAlign: 'center', color: '#495057' }}>{s.rakenneosa?.nimi ?? <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</td>
                  <td style={{ ...C, textAlign: 'center', fontWeight: 500 }}>{s.nayte_tunnus || <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</td>
                  {fieldEntries(t, patchArvo).map(f => <td key={f.label} style={C}>{f.node}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {/* kohdeId keyed off captionKohdeId (the already-loaded testi_caption
          row), not tkId — see PeitepaksuusView for why. */}
      <TaulukkoFooter taulukkoNum={taulukkoNum} kohdeId={captionKohdeId ?? 'preview'} caption={caption} onChange={commitCaption} />
    </div>
  );
}
